import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError, AxiosResponse } from 'axios';

import { LANGUAGE, LANGUAGE_MAP } from 'src/constants/language';
import {
  MAX_REPORTS_IN_SHOPPING_CART,
  ReportOrderFinalStatusList,
  ReportOrderStatus,
} from 'src/constants/report';
import type { Report, ReportBundlePayload } from 'src/types/report';
import type { LanguageInfo } from 'src/types/reportConfig';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import Modal from 'src/components/common/Modal';
import {
  getReportsBundlePayload,
  reportEqualPredicate,
  reportUnEqualFunction,
} from 'src/components/report/helper/helper';
import { ReportStatusContext } from 'src/components/report/OrgUnitReportsPage/ReportStatusContext';

export const PREGEN_MODULE_ID = 1;

export interface ShoppingCartProviderProps {
  children?: ReactNode;
  value?: Partial<ShoppingCartContextValue>;
}

export interface ShoppingCartContextValue {
  addItem: (item: Report) => boolean;
  canUseShoppingCart: boolean;
  clearCart: () => void;
  deleteItem: (item: Report) => void;
  downloadCustomReportBundle: () => void;
  downloadReportBundle: () => void;
  isLoading: boolean;
  replaceEquivalentReports: (reports: Report[]) => Report[];
  setShoppingCartLanguage: Dispatch<SetStateAction<LanguageInfo>>;
  shoppingCartItems: Report[];
  shoppingCartLanguage: LanguageInfo;
}

export const ShoppingCartContext = createContext<ShoppingCartContextValue>({
  addItem: () => true,
  canUseShoppingCart: true,
  clearCart: () => {},
  deleteItem: () => {},
  downloadCustomReportBundle: () => {},
  downloadReportBundle: () => {},
  isLoading: false,
  replaceEquivalentReports: () => [],
  setShoppingCartLanguage: () => {},
  shoppingCartItems: [],
  shoppingCartLanguage: LANGUAGE_MAP[LANGUAGE.EN],
});

export const ShoppingCartProvider: FC<ShoppingCartProviderProps> = ({
  children,
  value,
}) => {
  const { t } = useTranslation('report');
  const [isLoading, setIsLoading] = useState(false);
  const [shoppingCartItems, setShoppingCartItems] = useState<Report[]>([]);
  const [shoppingCartLanguage, setShoppingCartLanguage] = useState(
    LANGUAGE_MAP[LANGUAGE.EN],
  );
  const [errorMessage, setErrorMessage] = useState<{
    headline?: string;
    body: string;
  }>();
  const { reportsStatus } = useContext(ReportStatusContext);
  const canUseShoppingCart = true;
  const moduleRef = useRef<number>();

  useEffect(() => {
    // Checks on pending reports if their order status changes
    if (isLoading && moduleRef.current === PREGEN_MODULE_ID) {
      downloadReportBundle();
    }
  }, [reportsStatus]);

  /**
   * Add an item to the cart
   * @returns `true` if the item was added, `false` otherwise
   */
  const addItem = (item: Report) => {
    if (shoppingCartItems.length >= MAX_REPORTS_IN_SHOPPING_CART) {
      setErrorMessage({
        headline: t('shoppingCart.maximumNumberHeadline'),
        body: t('shoppingCart.maximumNumberMessage', {
          maxNumber: MAX_REPORTS_IN_SHOPPING_CART,
        }),
      });

      return false;
    }

    setShoppingCartItems([...shoppingCartItems, item]);

    return true;
  };

  /**
   * Find and remove a given item from the shoppingCartItems state.
   */
  const deleteItem = (item: Report) => {
    const differentReport = reportUnEqualFunction(item);

    const filteredItems = shoppingCartItems.filter(
      (_item: Report) => differentReport(_item) || _item.id !== item.id,
    );

    setShoppingCartItems(filteredItems);
  };

  /**
   * Replaces equivalent reports of a language in the shopping cart.
   * @param {Report[]} reports are either all reports or the reports of another language.
   * @return {Report[]} equivalent reports
   */
  const replaceEquivalentReports = (reports: Report[]): Report[] => {
    const equivalentReports: Report[] = [];
    const newShoppingCartItems = shoppingCartItems.reduce(
      (acc: Report[], cartItem) => {
        const equivalentReport = reports.find(
          (report) =>
            report.language === shoppingCartLanguage.code &&
            reportEqualPredicate(cartItem, report),
        );
        if (equivalentReport) {
          equivalentReports.push(equivalentReport);
          return [equivalentReport, ...acc];
        }
        return [cartItem, ...acc];
      },
      [],
    );

    setShoppingCartItems(newShoppingCartItems);
    return equivalentReports;
  };

  /**
   * On API success handler.
   */
  const onApiSuccess = (response: AxiosResponse) => {
    const anchorTag = document.createElement('a');

    anchorTag.setAttribute('href', response.data.url);
    anchorTag.setAttribute('download', 'Reports');
    anchorTag.style.display = 'none';

    document.body.appendChild(anchorTag);
    anchorTag.click();
    document.body.removeChild(anchorTag);
  };

  const postReportsBundle = (
    path: string,
    data: ReportBundlePayload | number[],
  ) => {
    HttpService.post(path, data)
      .then((response: AxiosResponse) => onApiSuccess(response))
      .catch((error: AxiosError<{ error: string }>) =>
        setErrorMessage({
          headline: t('shoppingCart.problemOccurredHeadline'),
          body:
            error?.response?.data?.error ||
            t('shoppingCart.problemOccurredMessage'),
        }),
      )
      .finally(() => setIsLoading(false));
  };

  /**
   * Returns all created reports.
   * @param partialSuccess returns all created reports even if some reports are not yet created
   */
  const getCreatedReports = (partialSuccess = false) => {
    const reports = shoppingCartItems.reduce((acc: Report[], cartItem) => {
      if (cartItem.order_status === ReportOrderStatus.CREATED)
        return [...acc, cartItem];
      else if (
        reportsStatus[cartItem.id]?.order_status === ReportOrderStatus.CREATED
      )
        return [
          ...acc,
          {
            ...cartItem,
            file: reportsStatus[cartItem.id].file || cartItem.file,
          },
        ];
      return acc;
    }, []);

    if (!reports.length) {
      setErrorMessage({
        headline: t('shoppingCart.noDoneReportsSelectedHeadline'),
        body: t('shoppingCart.noDoneReportsSelectedMessage'),
      });
      return;
    }

    // custom reports only
    if (!partialSuccess && reports.length !== shoppingCartItems.length) {
      setErrorMessage({
        headline: t('shoppingCart.cannotDownloadHeadline'),
        body: t('shoppingCart.cannotDownloadMessage'),
      });
      return;
    }
    return reports;
  };

  /**
   * Download Report Bundle
   */
  const downloadReportBundle = () => {
    if (!shoppingCartItems.length)
      return setErrorMessage({
        headline: t('shoppingCart.noReportsSelectedHeadline'),
        body: t('shoppingCart.noReportsSelectedMessage'),
      });

    // set moduleRef to determine what kind of download was triggered
    moduleRef.current = PREGEN_MODULE_ID;
    setIsLoading(true);

    if (
      !shoppingCartItems.every(({ id, order_status }) =>
        ReportOrderFinalStatusList.includes(
          reportsStatus[id]?.order_status || order_status,
        ),
      )
    ) {
      // return in case a report has not been processed yet
      return;
    }

    const reports = getCreatedReports(true);
    if (!reports) {
      setIsLoading(false);
      return;
    }

    postReportsBundle(
      `${apiPaths.GET_REPORTS_BUNDLE}${PREGEN_MODULE_ID}`,
      getReportsBundlePayload(reports, shoppingCartLanguage.code),
    );
  };

  /**
   * Download Custom Report Bundle
   */
  const downloadCustomReportBundle = () => {
    if (!shoppingCartItems.length)
      return setErrorMessage({
        headline: t('shoppingCart.noReportsSelectedHeadline'),
        body: t('shoppingCart.noReportsSelectedMessage'),
      });

    const reports = getCreatedReports();
    if (!reports) return;

    setIsLoading(true);

    postReportsBundle(
      apiPaths.GET_CUSTOM_REPORTS_BUNDLE,
      reports.map((report) => report.id),
    );
  };

  /**
   * Clear all items from shopping cart.
   */
  const clearCart = () => setShoppingCartItems([]);

  return (
    <>
      <ShoppingCartContext.Provider
        value={{
          addItem,
          canUseShoppingCart,
          clearCart,
          deleteItem,
          downloadCustomReportBundle,
          downloadReportBundle,
          isLoading,
          setShoppingCartLanguage,
          shoppingCartItems,
          shoppingCartLanguage,
          replaceEquivalentReports,
          ...value,
        }}
      >
        {children}
      </ShoppingCartContext.Provider>

      <Modal
        className="max-num-of-reports"
        headline={errorMessage?.headline}
        isOpen={!!errorMessage}
        message={errorMessage?.body}
        onClose={() => setErrorMessage(undefined)}
      />
    </>
  );
};

export const useShoppingCart = () => useContext(ShoppingCartContext);

export default ShoppingCartContext;
